import React, { useEffect, useState } from 'react';
import { Drawer, Modal, Skeleton, theme } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import type { MentionUser } from '../../../components/editor/RichTextEditor';
import type { TaskStore } from '../../../stores/kanban/TaskStore';
import type { KanbanTask } from '../../../stores/KanbanTaskStores';
import { createDirtyRegistry, DirtyGuardProvider } from './DirtyGuard';
import TaskHeader from './TaskHeader';
import TaskMain from './TaskMain';
import TaskSidebar from './TaskSidebar';
import type { AssignableUser } from './TaskSidebar';
import './task-details.css';

export type { AssignableUser };

export type TaskDetailsDrawerProps = {
  taskId: string | null;
  open: boolean;
  onClose: () => void;
  store: TaskStore;
  isAnonymous: boolean;
  isReadOnly: boolean;
  projectId: string;
  /** `@`-mention candidates for the description and comments; empty for anonymous access. */
  mentionUsers: MentionUser[];
  /** Candidates for the assignee select; empty (or omitted) for anonymous access. */
  assignableUsers?: AssignableUser[];
  /** Cloudinary base URL for avatars; the page passes `applicationStore.siteConfig.baseCloudinary`. */
  baseCloudinary: string;
  /** Id of the signed-in user — enables "Assign to me". */
  currentUserId?: string;
  /** Renders a user block for the author row; the page passes `getUserComponent(...)`. */
  renderUser?: (userId: string) => React.ReactNode;
  onDelete?: (taskId: string) => Promise<void>;
  /** Present only while the deleted-tasks view is open; its presence switches the header. */
  onRestore?: (taskId: string) => Promise<void>;
  onDeletePermanent?: (taskId: string) => Promise<void>;
  onCopyLink: (task: KanbanTask) => void;
  /** Filled into the Time tab's log-time description default; the tab falls back on its own otherwise. */
  projectName?: string;
};

/**
 * The task details drawer: a wide right-hand panel with a sticky header, the task itself on the
 * left and its properties on the right.
 *
 * It owns no task state — everything is read from `store` through mobx, so a card, the board and
 * this drawer always show the same task. The caller must have the task in the store before opening
 * (deep links fetch it first; see Task 8) — until then a skeleton is shown.
 */
export const TaskDetailsDrawer: React.FC<TaskDetailsDrawerProps> = observer(
  ({
    taskId,
    open,
    onClose,
    store,
    isAnonymous,
    isReadOnly,
    mentionUsers,
    assignableUsers,
    baseCloudinary,
    currentUserId,
    renderUser,
    onDelete,
    onRestore,
    onDeletePermanent,
    onCopyLink,
    projectName,
  }) => {
    const { token } = theme.useToken();
    const { t } = useTranslation();
    const [dirtyRegistry] = useState(createDirtyRegistry);

    useEffect(() => {
      if (!open || !taskId) {
        return;
      }
      // The stores report their own failures; a rejection here must not become an unhandled one.
      void store.loadTaskDetails(taskId).catch(() => undefined);
    }, [open, taskId, store]);

    const task = taskId ? store.getTask(taskId) : undefined;

    // The Drawer's mask click, Esc and header close button all end up here — the one place that
    // must not lose a description or comment draft the user hasn't saved yet.
    const requestClose = () => {
      if (!dirtyRegistry.hasDirty()) {
        onClose();
        return;
      }
      Modal.confirm({
        title: t('taskDrawerDiscardTitle'),
        okText: t('Discard'),
        cancelText: t('Cancel'),
        okButtonProps: { danger: true },
        onOk: () => onClose(),
      });
    };

    const palette = {
      '--td-bg': token.colorBgContainer,
      '--td-bg-subtle': token.colorFillQuaternary,
      '--td-border': token.colorBorderSecondary,
      '--td-text': token.colorText,
      '--td-text-secondary': token.colorTextSecondary,
      '--td-text-tertiary': token.colorTextTertiary,
      '--td-hover': token.colorFillTertiary,
      '--td-primary': token.colorPrimary,
      '--td-danger': token.colorError,
      '--td-radius': `${token.borderRadiusLG}px`,
    } as React.CSSProperties;

    return (
      <Drawer
        open={open}
        onClose={requestClose}
        placement="right"
        width="min(1240px, 96vw)"
        closable={false}
        destroyOnClose
        className="task-drawer"
        styles={{ body: { padding: 0 } }}
      >
        <div className="task-drawer__shell" style={palette}>
          {task ? (
            <DirtyGuardProvider registry={dirtyRegistry}>
              <TaskHeader
                task={task}
                store={store}
                readOnly={isReadOnly}
                isAnonymous={isAnonymous}
                isDeleted={!!onRestore}
                onClose={requestClose}
                onCopyLink={onCopyLink}
                onDelete={onDelete}
                onRestore={onRestore}
                onDeletePermanent={onDeletePermanent}
              />
              {/* Keyed on the task: switching tasks throws away every editor instance and its
                  draft instead of re-pointing them at a different task. */}
              <div className="task-drawer__body">
                <TaskMain
                  key={`main-${task.id}`}
                  task={task}
                  store={store}
                  readOnly={isReadOnly || !!onRestore}
                  isAnonymous={isAnonymous}
                  mentionUsers={mentionUsers}
                  currentUserId={currentUserId}
                  projectName={projectName}
                />
                <TaskSidebar
                  key={`sidebar-${task.id}`}
                  task={task}
                  store={store}
                  readOnly={isReadOnly || !!onRestore}
                  isAnonymous={isAnonymous}
                  assignableUsers={assignableUsers}
                  baseCloudinary={baseCloudinary}
                  currentUserId={currentUserId}
                  renderUser={renderUser}
                />
              </div>
            </DirtyGuardProvider>
          ) : (
            <div className="task-drawer__loading">
              <Skeleton active title paragraph={{ rows: 2 }} />
              <Skeleton active title={false} paragraph={{ rows: 8 }} />
            </div>
          )}
        </div>
      </Drawer>
    );
  },
);

export default TaskDetailsDrawer;
